import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faDownload,
  faCheck,
  faTimes,
  faEye,
  faCalendar,
  faGlobe,
  faSpinner,
  faExclamationTriangle,
  faFilm,
  faSyncAlt,
  faLink,
  faFileDownload
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminLeech.css';

const AdminLeech = () => {
  const [sources, setSources] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [leechJobs, setLeechJobs] = useState([]);

  // Mock leech sources
  const mockSources = [
    {
      id: 1,
      name: 'AnimeHay',
      url: 'https://animehay.tv',
      status: 'active',
      last_sync: '2024-01-20T10:30:00Z',
      total_movies: 1250,
      success_rate: 95.5
    },
    {
      id: 2,
      name: 'AnimeVsub',
      url: 'https://animevsub.tv',
      status: 'active',
      last_sync: '2024-01-20T09:15:00Z',
      total_movies: 890,
      success_rate: 92.3
    },
    {
      id: 3,
      name: 'HoatHinhTQ',
      url: 'https://hoathinhtq.com',
      status: 'inactive',
      last_sync: '2024-01-18T16:45:00Z',
      total_movies: 650,
      success_rate: 88.7
    }
  ];

  // Mock search results
  const mockSearchResults = [
    {
      id: 1,
      source_id: 1,
      source_name: 'AnimeHay',
      title: 'One Piece (2023)',
      original_url: 'https://animehay.tv/phim/one-piece-2023',
      poster_url: 'https://source.anime.com/poster1.jpg',
      year: 2023,
      episodes: 1000,
      status: 'ongoing',
      description: 'Câu chuyện về Monkey D. Luffy và băng hải tặc Mũ Rơm...',
      rating: 9.5,
      view_count: 2500000,
      already_exists: false,
      can_leech: true,
      last_updated: '2024-01-20T08:00:00Z'
    },
    {
      id: 2,
      source_id: 1,
      source_name: 'AnimeHay',
      title: 'Attack on Titan Final Season',
      original_url: 'https://animehay.tv/phim/attack-on-titan-final',
      poster_url: 'https://source.anime.com/poster2.jpg',
      year: 2023,
      episodes: 28,
      status: 'completed',
      description: 'Phần cuối của series Attack on Titan...',
      rating: 9.2,
      view_count: 1800000,
      already_exists: true,
      can_leech: false,
      last_updated: '2024-01-19T14:30:00Z'
    },
    {
      id: 3,
      source_id: 2,
      source_name: 'AnimeVsub',
      title: 'Demon Slayer: Hashira Training Arc',
      original_url: 'https://animevsub.tv/phim/demon-slayer-hashira',
      poster_url: 'https://source.anime.com/poster3.jpg',
      year: 2024,
      episodes: 8,
      status: 'ongoing',
      description: 'Arc mới nhất của Demon Slayer về việc huấn luyện...',
      rating: 8.9,
      view_count: 1200000,
      already_exists: false,
      can_leech: true,
      last_updated: '2024-01-20T07:20:00Z'
    }
  ];

  // Mock leech jobs
  const mockLeechJobs = [
    {
      id: 1,
      movie_title: 'One Piece (2023)',
      source_url: 'https://animehay.tv/phim/one-piece-2023',
      status: 'downloading',
      progress: 75,
      total_episodes: 50,
      downloaded_episodes: 38,
      start_time: '2024-01-20T10:00:00Z',
      estimated_completion: '2024-01-20T12:30:00Z',
      error_message: null
    },
    {
      id: 2,
      movie_title: 'Jujutsu Kaisen Season 2',
      source_url: 'https://animevsub.tv/phim/jujutsu-kaisen-s2',
      status: 'completed',
      progress: 100,
      total_episodes: 23,
      downloaded_episodes: 23,
      start_time: '2024-01-19T09:00:00Z',
      completion_time: '2024-01-19T15:45:00Z',
      error_message: null
    },
    {
      id: 3,
      movie_title: 'Tokyo Revengers Season 3',
      source_url: 'https://animehay.tv/phim/tokyo-revengers-s3',
      status: 'failed',
      progress: 15,
      total_episodes: 13,
      downloaded_episodes: 2,
      start_time: '2024-01-18T14:20:00Z',
      error_message: 'Connection timeout after multiple retries'
    }
  ];

  useEffect(() => {
    // Simulate API calls
    setTimeout(() => {
      setSources(mockSources);
      setLeechJobs(mockLeechJobs);
    }, 500);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    // Simulate search API call
    setTimeout(() => {
      setSearchResults(mockSearchResults.filter(result => 
        result.title.toLowerCase().includes(searchTerm.toLowerCase())
      ));
      setLoading(false);
    }, 2000);
  };

  const handleLeechMovie = (movie) => {
    if (!movie.can_leech) return;
    
    const newJob = {
      id: Date.now(),
      movie_title: movie.title,
      source_url: movie.original_url,
      status: 'queued',
      progress: 0,
      total_episodes: movie.episodes,
      downloaded_episodes: 0,
      start_time: new Date().toISOString(),
      estimated_completion: null,
      error_message: null
    };
    
    setLeechJobs([newJob, ...leechJobs]);
    
    // Simulate job progression
    setTimeout(() => {
      setLeechJobs(jobs => jobs.map(job => 
        job.id === newJob.id 
          ? { ...job, status: 'downloading', progress: 10 }
          : job
      ));
    }, 1000);
  };

  const handleCancelJob = (jobId) => {
    setLeechJobs(leechJobs.map(job => 
      job.id === jobId 
        ? { ...job, status: 'cancelled' }
        : job
    ));
  };

  const handleRetryJob = (jobId) => {
    setLeechJobs(leechJobs.map(job => 
      job.id === jobId 
        ? { ...job, status: 'queued', progress: 0, error_message: null }
        : job
    ));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'queued': return '#f59e0b';
      case 'downloading': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'failed': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#9ca3af';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'queued': return 'Đang chờ';
      case 'downloading': return 'Đang tải';
      case 'completed': return 'Hoàn thành';
      case 'failed': return 'Thất bại';
      case 'cancelled': return 'Đã hủy';
      default: return 'Không xác định';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  // eslint-disable-next-line no-unused-vars
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="admin-leech">
      <div className="leech-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faFileDownload} />
            Leech Phim
          </h1>
          <p>Tìm kiếm và tải phim từ các nguồn bên ngoài</p>
        </div>
      </div>

      {/* Sources Status */}
      <div className="sources-status">
        <h3>Nguồn Leech</h3>
        <div className="sources-grid">
          {sources.map(source => (
            <div key={source.id} className={`source-card ${source.status}`}>
              <div className="source-header">
                <div className="source-info">
                  <h4>{source.name}</h4>
                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faGlobe} />
                    {source.url}
                  </a>
                </div>
                <div className={`source-status ${source.status}`}>
                  {source.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                </div>
              </div>
              <div className="source-stats">
                <div className="stat">
                  <span className="stat-label">Tổng phim:</span>
                  <span className="stat-value">{source.total_movies}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Tỷ lệ thành công:</span>
                  <span className="stat-value">{source.success_rate}%</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Sync cuối:</span>
                  <span className="stat-value">{formatDate(source.last_sync)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <h3>Tìm kiếm Phim</h3>
        <div className="search-controls">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Nhập tên phim cần tìm..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <select 
            value={selectedSource} 
            onChange={(e) => setSelectedSource(e.target.value)}
          >
            <option value="all">Tất cả nguồn</option>
            {sources.filter(s => s.status === 'active').map(source => (
              <option key={source.id} value={source.id}>{source.name}</option>
            ))}
          </select>
          <button 
            className="search-btn" 
            onClick={handleSearch}
            disabled={loading || !searchTerm.trim()}
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )}
            Tìm kiếm
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="search-results">
          <h3>Kết quả tìm kiếm ({searchResults.length})</h3>
          <div className="results-grid">
            {searchResults.map(movie => (
              <div key={movie.id} className="movie-result-card">
                <div className="movie-poster">
                  <img 
                    src={movie.poster_url} 
                    alt={movie.title}
                    onError={(e) => {
                      e.target.src = '/logo.png';
                    }}
                  />
                  {movie.already_exists && (
                    <div className="exists-badge">
                      <FontAwesomeIcon icon={faCheck} />
                      Đã có
                    </div>
                  )}
                </div>
                <div className="movie-info">
                  <h4 className="movie-title">{movie.title}</h4>
                  <div className="movie-meta">
                    <span className="source-name">
                      <FontAwesomeIcon icon={faGlobe} />
                      {movie.source_name}
                    </span>
                    <span className="movie-year">{movie.year}</span>
                    <span className="movie-episodes">
                      <FontAwesomeIcon icon={faFilm} />
                      {movie.episodes} tập
                    </span>
                  </div>
                  <div className="movie-stats">
                    <span className="rating">
                      <FontAwesomeIcon icon={faEye} />
                      {movie.rating}/10
                    </span>
                    <span className="views">
                      {(movie.view_count / 1000000).toFixed(1)}M views
                    </span>
                  </div>
                  <p className="movie-description">
                    {movie.description.length > 100 
                      ? movie.description.substring(0, 100) + '...'
                      : movie.description
                    }
                  </p>
                  <div className="movie-actions">
                    <a 
                      href={movie.original_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="action-btn view"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      Xem gốc
                    </a>
                    <button 
                      className={`action-btn leech ${!movie.can_leech ? 'disabled' : ''}`}
                      onClick={() => handleLeechMovie(movie)}
                      disabled={!movie.can_leech}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      {movie.already_exists ? 'Đã có' : 'Leech'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leech Jobs */}
      <div className="leech-jobs">
        <h3>Công việc Leech ({leechJobs.length})</h3>
        <div className="jobs-table-container">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Phim</th>
                <th>Nguồn</th>
                <th>Trạng thái</th>
                <th>Tiến độ</th>
                <th>Tập phim</th>
                <th>Thời gian</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {leechJobs.map(job => (
                <tr key={job.id} className={`job-${job.status}`}>
                  <td>
                    <div className="job-movie">
                      <FontAwesomeIcon icon={faFilm} />
                      {job.movie_title}
                    </div>
                  </td>
                  <td>
                    <div className="job-source">
                      <FontAwesomeIcon icon={faLink} />
                      <a href={job.source_url} target="_blank" rel="noopener noreferrer">
                        Nguồn gốc
                      </a>
                    </div>
                  </td>
                  <td>
                    <div 
                      className="job-status"
                      style={{ backgroundColor: getStatusColor(job.status) }}
                    >
                      {getStatusText(job.status)}
                    </div>
                  </td>
                  <td>
                    <div className="job-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${job.progress}%`,
                            backgroundColor: getStatusColor(job.status)
                          }}
                        ></div>
                      </div>
                      <span className="progress-text">{job.progress}%</span>
                    </div>
                  </td>
                  <td>
                    <div className="job-episodes">
                      {job.downloaded_episodes}/{job.total_episodes}
                    </div>
                  </td>
                  <td>
                    <div className="job-time">
                      <div className="start-time">
                        <FontAwesomeIcon icon={faCalendar} />
                        {formatDate(job.start_time)}
                      </div>
                      {job.completion_time && (
                        <div className="completion-time">
                          Hoàn thành: {formatDate(job.completion_time)}
                        </div>
                      )}
                      {job.error_message && (
                        <div className="error-message">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          {job.error_message}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="job-actions">
                      {job.status === 'downloading' && (
                        <button 
                          className="action-btn cancel"
                          onClick={() => handleCancelJob(job.id)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                      {job.status === 'failed' && (
                        <button 
                          className="action-btn retry"
                          onClick={() => handleRetryJob(job.id)}
                        >
                          <FontAwesomeIcon icon={faSyncAlt} />
                        </button>
                      )}
                      {job.status === 'completed' && (
                        <span className="success-mark">
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminLeech;